import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { photoUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { 
  Shield, Trash2, Key, Bell, Phone, AlertTriangle, 
  Lock, Unlock, Check, EyeOff, Eye, Smartphone 
} from "lucide-react";

export default function Privacy() {
  const { logout } = useAuth();
  const navigate = useNavigate();

  // Settings states
  const [hidePhoto, setHidePhoto] = useState(false);
  const [hidePhone, setHidePhone] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Blocked users states
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [loadingBlocked, setLoadingBlocked] = useState(true);

  // Change phone OTP states
  const [newPhone, setNewPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [loadingOtp, setLoadingOtp] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // Notification preferences state (mock)
  const [notifPrefs, setNotifPrefs] = useState({
    email_interest: true,
    email_messages: true,
    email_views: true,
    sms_interest: true
  });

  // Fetch current user details & blocked list
  useEffect(() => {
    // We can fetch from auth/me or current profile details to prefill toggles
    api.get("/auth/me")
      .then(({ data }) => {
        setHidePhoto(!!data.hide_photo);
        setHidePhone(!!data.hide_phone);
        setLoadingSettings(false);
      })
      .catch(() => {
        setLoadingSettings(false);
      });

    fetchBlockedUsers();
  }, []);

  const fetchBlockedUsers = () => {
    setLoadingBlocked(true);
    api.get("/privacy/blocked")
      .then(({ data }) => {
        setBlockedUsers(data);
        setLoadingBlocked(false);
      })
      .catch(() => {
        setLoadingBlocked(false);
      });
  };

  // Toggle settings
  const handleSaveSettings = async (field, val) => {
    try {
      const payload = {
        hide_photo: field === "hide_photo" ? val : hidePhoto,
        hide_phone: field === "hide_phone" ? val : hidePhone
      };
      
      if (field === "hide_photo") setHidePhoto(val);
      if (field === "hide_phone") setHidePhone(val);

      await api.post("/privacy/settings", payload);
      toast.success("Privacy preferences updated successfully.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update settings.");
    }
  };

  // Unblock user
  const handleUnblock = async (targetId) => {
    try {
      await api.post(`/privacy/unblock/${targetId}`);
      toast.success("User has been unblocked.");
      fetchBlockedUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unblock user.");
    }
  };

  // Send Change Phone OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!newPhone.startsWith("+91") || newPhone.length !== 13) {
      toast.error("Please enter a valid India phone number starting with +91 (e.g. +919876543210)");
      return;
    }
    setLoadingOtp(true);
    try {
      await api.post("/privacy/phone/send-otp", { phone: newPhone });
      setOtpSent(true);
      toast.success(`Verification code sent to ${newPhone}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send verification code.");
    } finally {
      setLoadingOtp(false);
    }
  };

  // Verify Change Phone OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 4 || otp.length > 6) {
      toast.error("Please enter the 4 to 6-digit verification code");
      return;
    }
    setLoadingOtp(true);
    try {
      await api.post("/privacy/phone/verify-otp", { phone: newPhone, otp });
      toast.success("Phone number changed successfully.");
      setOtpSent(false);
      setNewPhone("");
      setOtp("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed. Please check the code.");
    } finally {
      setLoadingOtp(false);
    }
  };

  // Delete Account
  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== "delete my account") {
      toast.error("Please type the confirmation phrase exactly to proceed");
      return;
    }
    try {
      await api.post("/privacy/delete");
      toast.success("Your account has been deleted permanently.");
      setShowDeleteModal(false);
      await logout();
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete account.");
    }
  };

  return (
    <div className="container-px py-8 max-w-4xl mx-auto">
      <span className="label-cap">Settings</span>
      <h1 className="font-heading text-4xl text-primary mt-1 flex items-center gap-2">
        <Shield className="w-8 h-8 text-secondary" strokeWidth={1.5} />
        Privacy & Account Controls
      </h1>
      <p className="text-muted-foreground mt-2">Manage what information you share, control your visibility, and secure your account.</p>

      {/* Main Settings Sections */}
      <div className="mt-8 space-y-8">
        
        {/* Toggle Visibilities */}
        <section className="card-soft p-6">
          <h2 className="font-heading text-2xl text-primary flex items-center gap-2 mb-4">
            <Lock className="w-5 h-5 text-secondary" />
            Profile Visibility Settings
          </h2>
          {loadingSettings ? (
            <div className="text-sm text-muted-foreground">Loading visibility settings...</div>
          ) : (
            <div className="divide-y divide-border/60">
              {/* Hide Photo */}
              <div className="py-4 flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg text-primary flex items-center gap-2">
                    {hidePhoto ? <EyeOff className="w-4 h-4 text-muted-foreground" /> : <Eye className="w-4 h-4 text-secondary" />}
                    Hide Photo to Guests
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                    When enabled, your profile photos will be hidden from guests and non-premium members who do not have a mutual interest match with you.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={hidePhoto} 
                    onChange={(e) => handleSaveSettings("hide_photo", e.target.checked)}
                    className="sr-only peer" 
                    data-testid="toggle-hide-photo"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                </label>
              </div>

              {/* Hide Phone */}
              <div className="py-4 flex items-center justify-between">
                <div>
                  <div className="font-heading text-lg text-primary flex items-center gap-2">
                    <Phone className="w-4 h-4 text-secondary" />
                    Hide Phone Number
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 max-w-lg">
                    Hide your phone number from premium searchers. It will only be visible to users once you accept or match with each other mutually.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={hidePhone} 
                    onChange={(e) => handleSaveSettings("hide_phone", e.target.checked)}
                    className="sr-only peer"
                    data-testid="toggle-hide-phone"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-background after:border-border after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-secondary"></div>
                </label>
              </div>
            </div>
          )}
        </section>

        {/* Change Phone Number Flow */}
        <section className="card-soft p-6">
          <h2 className="font-heading text-2xl text-primary flex items-center gap-2 mb-4">
            <Smartphone className="w-5 h-5 text-secondary" />
            Update Registered Mobile Number
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            To change your mobile number, enter the new number starting with +91. We will send a one-time verification code (OTP) via SMS to verify ownership.
          </p>

          {!otpSent ? (
            <form onSubmit={handleSendOtp} className="max-w-md flex flex-col sm:flex-row gap-3">
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+919876543210"
                className="input-text rounded-full px-4 py-2 text-sm flex-1"
                required
                data-testid="new-phone-input"
              />
              <Button 
                type="submit" 
                disabled={loadingOtp}
                className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground"
                data-testid="send-otp-btn"
              >
                {loadingOtp ? "Sending..." : "Send Verification OTP"}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="max-w-md space-y-3">
              <div>
                <p className="text-xs font-semibold text-secondary mb-1">Verify Code sent to {newPhone}</p>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder="Enter Verification Code"
                  className="input-text rounded-full px-4 py-2 text-sm w-full"
                  required
                  data-testid="otp-input"
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  type="submit" 
                  disabled={loadingOtp}
                  className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground flex-1"
                  data-testid="verify-otp-btn"
                >
                  {loadingOtp ? "Verifying..." : "Verify & Commit Change"}
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setOtpSent(false)}
                  className="rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </section>

        {/* Blocked Users Manager */}
        <section className="card-soft p-6">
          <h2 className="font-heading text-2xl text-primary flex items-center gap-2 mb-4">
            <Unlock className="w-5 h-5 text-secondary" />
            Blocked Users List
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Blocked members cannot find your profile, view photos, or send messages/interests.
          </p>

          {loadingBlocked ? (
            <div className="text-sm text-muted-foreground">Loading blocked list...</div>
          ) : blockedUsers.length === 0 ? (
            <div className="text-sm text-muted-foreground italic border border-dashed rounded-xl p-6 text-center">
              You haven't blocked any users yet.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {blockedUsers.map((b) => (
                <div key={b.user_id} className="border border-border/60 rounded-xl p-3 flex items-center justify-between gap-3 bg-card">
                  <div className="flex items-center gap-3 min-w-0">
                    <img 
                      src={photoUrl(b.photos?.[0]) || "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&auto=format&fit=crop&q=40"} 
                      alt="" 
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="min-w-0">
                      <div className="font-heading text-sm text-primary truncate font-semibold">{b.name}</div>
                      <div className="text-[10px] text-muted-foreground">{b.age ? `${b.age} yrs · ` : ""}{b.district}</div>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs text-secondary hover:bg-secondary/10 px-2 py-1 h-auto rounded-full"
                    onClick={() => handleUnblock(b.user_id)}
                    data-testid={`unblock-${b.user_id}`}
                  >
                    Unblock
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Notifications Preferences (Local/Mock settings) */}
        <section className="card-soft p-6">
          <h2 className="font-heading text-2xl text-primary flex items-center gap-2 mb-4">
            <Bell className="w-5 h-5 text-secondary" />
            Notification Settings
          </h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="email_interest"
                checked={notifPrefs.email_interest}
                onChange={(e) => setNotifPrefs({...notifPrefs, email_interest: e.target.checked})}
                className="rounded text-secondary focus:ring-secondary border-border"
              />
              <label htmlFor="email_interest" className="text-sm text-primary">Receive Email notifications for interest matches</label>
            </div>
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="email_messages"
                checked={notifPrefs.email_messages}
                onChange={(e) => setNotifPrefs({...notifPrefs, email_messages: e.target.checked})}
                className="rounded text-secondary focus:ring-secondary border-border"
              />
              <label htmlFor="email_messages" className="text-sm text-primary">Receive Email notifications for new chat messages</label>
            </div>
            <div className="flex items-center gap-3">
              <input 
                type="checkbox" 
                id="sms_interest"
                checked={notifPrefs.sms_interest}
                onChange={(e) => setNotifPrefs({...notifPrefs, sms_interest: e.target.checked})}
                className="rounded text-secondary focus:ring-secondary border-border"
              />
              <label htmlFor="sms_interest" className="text-sm text-primary">Enable SMS alerts for mutual matches</label>
            </div>
          </div>
        </section>

        {/* Delete Profile */}
        <section className="border border-red-200/40 bg-red-50/5 dark:bg-red-950/5 rounded-2xl p-6">
          <h2 className="font-heading text-2xl text-red-600 flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5" />
            Delete Account Permanently
          </h2>
          <p className="text-sm text-muted-foreground">
            Once you delete your profile, all matching records, photos, chat messages, and premium membership details will be erased permanently. This action cannot be undone.
          </p>
          <Button 
            onClick={() => setShowDeleteModal(true)}
            className="mt-4 rounded-full bg-red-600 hover:bg-red-700 text-white"
            data-testid="delete-profile-trigger"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete Profile
          </Button>
        </section>

      </div>

      {/* Confirmation modal for delete account */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-card border border-border rounded-2xl max-w-md w-full p-6 shadow-xl relative animate-in zoom-in-95 duration-200">
            <h3 className="font-heading text-xl text-primary flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Are you absolutely sure?
            </h3>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              This action is final. All your private chats, search rankings, uploaded photos, and matching scores will be wiped.
            </p>
            <div className="mt-4 p-3 bg-muted rounded-xl text-xs text-muted-foreground">
              To confirm, please type <strong className="text-primary select-none">delete my account</strong> in the field below.
            </div>
            
            <input 
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="delete my account"
              className="input-text rounded-full px-4 py-2 text-sm mt-3 w-full border-red-300 focus:border-red-500 focus:ring-red-500"
              data-testid="delete-confirm-input"
            />

            <div className="mt-6 flex justify-end gap-3">
              <Button 
                variant="outline" 
                className="rounded-full" 
                onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(""); }}
              >
                Cancel
              </Button>
              <Button 
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText.toLowerCase() !== "delete my account"}
                data-testid="delete-profile-confirm"
              >
                Permanently Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
